/**
 * AdminAuditLogPage
 * UC-014: Admin Audit Log View
 *
 * @screen AdminAuditLog
 * @role RL-001
 * @usecases UC-014
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { AuditLogListResponse, UserListResponse, PaginatedResponse } from '../types';
import { api } from '../core/api-client';

interface AuditLogEntry {
  id: string;
  timestamp: string;
  userId: number;
  actionType: string;
  entityAffected: string;
  details: string;
}

interface User {
  userId: number;
  name: string;
  surname: string;
  email: string;
}

export function AdminAuditLogPage() {
  const navigate = useNavigate();

  // State for filters
  const [startDateValue, setStartDateValue] = useState<string>('');
  const [endDateValue, setEndDateValue] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedActionType, setSelectedActionType] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<string>('');

  // State for data
  const [auditLogEntries, setAuditLogEntries] = useState<AuditLogEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userEmailMap, setUserEmailMap] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Date picker state
  const [showStartDatePicker, setShowStartDatePicker] = useState<boolean>(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState<boolean>(false);
  const [startCalendarMonth, setStartCalendarMonth] = useState<number>(new Date().getMonth());
  const [startCalendarYear, setStartCalendarYear] = useState<number>(new Date().getFullYear());
  const [endCalendarMonth, setEndCalendarMonth] = useState<number>(new Date().getMonth());
  const [endCalendarYear, setEndCalendarYear] = useState<number>(new Date().getFullYear());

  // Calendar computed values
  const startCalendarMonthLabel = useMemo(() => {
    return new Date(startCalendarYear, startCalendarMonth).toLocaleDateString('en', {
      month: 'long',
      year: 'numeric'
    });
  }, [startCalendarYear, startCalendarMonth]);

  const endCalendarMonthLabel = useMemo(() => {
    return new Date(endCalendarYear, endCalendarMonth).toLocaleDateString('en', {
      month: 'long',
      year: 'numeric'
    });
  }, [endCalendarYear, endCalendarMonth]);

  const startCalendarDays = useMemo(() => {
    const first = new Date(startCalendarYear, startCalendarMonth, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(startCalendarYear, startCalendarMonth + 1, 0).getDate();
    const today = new Date();
    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push({ key: 'e' + i, date: null, label: '' });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(startCalendarYear, startCalendarMonth, d);
      const dateStr = date.toISOString().slice(0, 10);
      days.push({
        key: dateStr,
        date: dateStr,
        label: d,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: dateStr === startDateValue
      });
    }

    return days;
  }, [startCalendarYear, startCalendarMonth, startDateValue]);

  const endCalendarDays = useMemo(() => {
    const first = new Date(endCalendarYear, endCalendarMonth, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(endCalendarYear, endCalendarMonth + 1, 0).getDate();
    const today = new Date();
    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push({ key: 'e' + i, date: null, label: '' });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(endCalendarYear, endCalendarMonth, d);
      const dateStr = date.toISOString().slice(0, 10);
      days.push({
        key: dateStr,
        date: dateStr,
        label: d,
        isToday: date.toDateString() === today.toDateString(),
        isSelected: dateStr === endDateValue
      });
    }

    return days;
  }, [endCalendarYear, endCalendarMonth, endDateValue]);

  // Calendar navigation functions
  const prevStartMonth = () => {
    if (startCalendarMonth === 0) {
      setStartCalendarMonth(11);
      setStartCalendarYear(startCalendarYear - 1);
    } else {
      setStartCalendarMonth(startCalendarMonth - 1);
    }
  };

  const nextStartMonth = () => {
    if (startCalendarMonth === 11) {
      setStartCalendarMonth(0);
      setStartCalendarYear(startCalendarYear + 1);
    } else {
      setStartCalendarMonth(startCalendarMonth + 1);
    }
  };

  const prevEndMonth = () => {
    if (endCalendarMonth === 0) {
      setEndCalendarMonth(11);
      setEndCalendarYear(endCalendarYear - 1);
    } else {
      setEndCalendarMonth(endCalendarMonth - 1);
    }
  };

  const nextEndMonth = () => {
    if (endCalendarMonth === 11) {
      setEndCalendarMonth(0);
      setEndCalendarYear(endCalendarYear + 1);
    } else {
      setEndCalendarMonth(endCalendarMonth + 1);
    }
  };

  // Date selection functions
  const selectStartDate = (d: string) => {
    setStartDateValue(d);
    setShowStartDatePicker(false);
  };

  const selectEndDate = (d: string) => {
    setEndDateValue(d);
    setShowEndDatePicker(false);
  };

  const selectStartToday = () => {
    const t = new Date().toISOString().slice(0, 10);
    setStartDateValue(t);
    setShowStartDatePicker(false);
  };

  const selectEndToday = () => {
    const t = new Date().toISOString().slice(0, 10);
    setEndDateValue(t);
    setShowEndDatePicker(false);
  };

  const clearStartDate = () => {
    setStartDateValue('');
    setShowStartDatePicker(false);
  };

  const clearEndDate = () => {
    setEndDateValue('');
    setShowEndDatePicker(false);
  };

  // Filter options
  const actionTypeOptions = [
    'All Actions',
    'Status Update',
    'User Edit',
    'Project Create',
    'Project Delete',
    'Settings Change'
  ];
  const entityOptions = [
    'All Entities',
    'Project',
    'User',
    'Settings',
    'Payment'
  ];

  // API functions
  const fetchUsers = useCallback(async () => {
    try {
      // api client attaches the Bearer token + handles refresh. baseURL
      // already includes /api/v1, so the path starts at /users.
      // Default page size is 20; bump to the backend max so the filter
      // dropdown + audit-row user lookup resolve every user, not just
      // those on the first page.
      const data = await api.get<PaginatedResponse<UserListResponse>>('/users', { params: { limit: 100 } });
      const usersData = data.items.map(user => ({
        userId: user.userId,
        name: user.name,
        surname: user.surname,
        email: user.email
      }));

      setUsers(usersData);

      // Create email lookup map
      const emailMap: Record<number, string> = {};
      usersData.forEach(user => {
        emailMap[user.userId] = user.email;
      });
      setUserEmailMap(emailMap);

    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage('Failed to load users');
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const params: Record<string, string | number> = {};
      if (startDateValue) params.startDate = startDateValue;
      if (endDateValue) params.endDate = endDateValue;
      if (selectedUser) params.userId = selectedUser;
      if (selectedActionType && selectedActionType !== 'All Actions') {
        params.actionType = selectedActionType;
      }
      if (selectedEntity && selectedEntity !== 'All Entities') {
        params.entity = selectedEntity;
      }

      const data = await api.get<PaginatedResponse<AuditLogListResponse>>(
        '/audit-logs',
        { params },
      );
      // Backend AuditLogResponse stores the JSON blob under `changes`; the
      // shared TS type calls it `details` (legacy spec mash-up). Read both.
      const auditData = data.items.map((log: any) => {
        const blob = log.changes ?? log.details ?? null;
        return {
          id: String(log.id ?? `${log.userId}-${log.timestamp}`),
          timestamp: new Date(log.timestamp).toLocaleString(),
          userId: log.userId,
          actionType: log.action,
          entityAffected: `${log.entityType} #${log.entityId}`,
          details: blob == null ? '' : typeof blob === 'object' ? JSON.stringify(blob) : String(blob),
        };
      });

      setAuditLogEntries(auditData);

    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setErrorMessage('Failed to load audit logs');
      toast.error('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [startDateValue, endDateValue, selectedUser, selectedActionType, selectedEntity]);

  // Load initial data
  useEffect(() => {
    fetchUsers();
    fetchAuditLogs();
  }, [fetchUsers, fetchAuditLogs]);

  const getUserEmail = (userId: number): string => {
    return userEmailMap[userId] || `User ${userId}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">Audit Log</h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">
          View and filter system audit log entries
        </p>
      </div>

      {/* Filters */}
      <div className="mb-5 sm:mb-6 p-4 sm:p-4 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] overflow-visible">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-visible">

          {/* Date Range Filter - Start Date */}
          <div className="relative">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Start Date
            </label>
            <button
              onClick={() => setShowStartDatePicker(!showStartDatePicker)}
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-base sm:text-sm text-left focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
            >
              <span className={startDateValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}>
                {startDateValue || 'Start date'}
              </span>
              <svg className="w-4 h-4 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>

            {showStartDatePicker && (
              <div className="absolute z-50 top-full mt-1 w-72 bg-[var(--surface-overlay)] rounded-xl border border-[var(--border-subtle)] shadow-xl p-3">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={prevStartMonth} className="p-2 hover:bg-[var(--surface-raised)] rounded text-[var(--text-secondary)]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                  </button>
                  <span className="text-sm font-medium text-[var(--text-primary)]">{startCalendarMonthLabel}</span>
                  <button onClick={nextStartMonth} className="p-2 hover:bg-[var(--surface-raised)] rounded text-[var(--text-secondary)]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs text-[var(--text-muted)] mb-1">
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <span key={d}>{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {startCalendarDays.map((day: any) => (
                    <button
                      key={day.key}
                      onClick={() => day.date && selectStartDate(day.date)}
                      disabled={!day.date}
                      className={`h-8 w-8 rounded-lg text-xs transition-colors flex items-center justify-center mx-auto ${
                        !day.date ? 'invisible' : ''
                      } ${
                        day.isSelected ? 'bg-[var(--accent-primary)] text-white font-semibold' : ''
                      } ${
                        day.isToday && !day.isSelected ? 'border border-[var(--accent-primary)] text-[var(--accent-primary)]' : ''
                      } ${
                        !day.isSelected && !day.isToday && day.date ? 'text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]' : ''
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-[var(--border-subtle)] flex justify-between">
                  <button
                    onClick={selectStartToday}
                    className="px-2 py-1.5 text-xs text-[var(--accent-primary)] font-medium rounded hover:bg-[var(--surface-raised)]"
                  >
                    Today
                  </button>
                  <button
                    onClick={clearStartDate}
                    className="px-2 py-1.5 text-xs text-[var(--text-muted)] rounded hover:bg-[var(--surface-raised)]"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Date Range Filter - End Date */}
          <div className="relative">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              End Date
            </label>
            <button
              onClick={() => setShowEndDatePicker(!showEndDatePicker)}
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-base sm:text-sm text-left focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
            >
              <span className={endDateValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'}>
                {endDateValue || 'End date'}
              </span>
              <svg className="w-4 h-4 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>

            {showEndDatePicker && (
              <div className="absolute z-50 top-full mt-1 w-72 bg-[var(--surface-overlay)] rounded-xl border border-[var(--border-subtle)] shadow-xl p-3">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={prevEndMonth} className="p-2 hover:bg-[var(--surface-raised)] rounded text-[var(--text-secondary)]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                  </button>
                  <span className="text-sm font-medium text-[var(--text-primary)]">{endCalendarMonthLabel}</span>
                  <button onClick={nextEndMonth} className="p-2 hover:bg-[var(--surface-raised)] rounded text-[var(--text-secondary)]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs text-[var(--text-muted)] mb-1">
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <span key={d}>{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {endCalendarDays.map((day: any) => (
                    <button
                      key={day.key}
                      onClick={() => day.date && selectEndDate(day.date)}
                      disabled={!day.date}
                      className={`h-8 w-8 rounded-lg text-xs transition-colors flex items-center justify-center mx-auto ${
                        !day.date ? 'invisible' : ''
                      } ${
                        day.isSelected ? 'bg-[var(--accent-primary)] text-white font-semibold' : ''
                      } ${
                        day.isToday && !day.isSelected ? 'border border-[var(--accent-primary)] text-[var(--accent-primary)]' : ''
                      } ${
                        !day.isSelected && !day.isToday && day.date ? 'text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]' : ''
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-[var(--border-subtle)] flex justify-between">
                  <button
                    onClick={selectEndToday}
                    className="px-2 py-1.5 text-xs text-[var(--accent-primary)] font-medium rounded hover:bg-[var(--surface-raised)]"
                  >
                    Today
                  </button>
                  <button
                    onClick={clearEndDate}
                    className="px-2 py-1.5 text-xs text-[var(--text-muted)] rounded hover:bg-[var(--surface-raised)]"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Filter */}
          <div className="relative w-full sm:min-w-[180px] sm:w-auto">
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">User</label>
            <select
              value={selectedUser || ''}
              onChange={(e) => setSelectedUser(e.target.value ? parseInt(e.target.value) : null)}
              className="relative w-full cursor-pointer rounded-lg bg-[var(--surface-raised)] py-3 sm:py-2.5 pl-4 pr-10 text-left text-[var(--text-primary)] ring-1 ring-inset ring-[var(--border-subtle)] text-base sm:text-sm"
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.userId} value={user.userId}>
                  {user.email}
                </option>
              ))}
            </select>
          </div>

          {/* Action Type Filter */}
          <div className="relative w-full sm:min-w-[180px] sm:w-auto">
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">Action Type</label>
            <select
              value={selectedActionType}
              onChange={(e) => setSelectedActionType(e.target.value)}
              className="relative w-full cursor-pointer rounded-lg bg-[var(--surface-raised)] py-3 sm:py-2.5 pl-4 pr-10 text-left text-[var(--text-primary)] ring-1 ring-inset ring-[var(--border-subtle)] text-base sm:text-sm"
            >
              {actionTypeOptions.map(actionType => (
                <option key={actionType} value={actionType}>
                  {actionType}
                </option>
              ))}
            </select>
          </div>

          {/* Entity Filter */}
          <div className="relative w-full sm:min-w-[180px] sm:w-auto">
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1.5">Entity</label>
            <select
              value={selectedEntity}
              onChange={(e) => setSelectedEntity(e.target.value)}
              className="relative w-full cursor-pointer rounded-lg bg-[var(--surface-raised)] py-3 sm:py-2.5 pl-4 pr-10 text-left text-[var(--text-primary)] ring-1 ring-inset ring-[var(--border-subtle)] text-base sm:text-sm"
            >
              {entityOptions.map(entity => (
                <option key={entity} value={entity}>
                  {entity}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-8 text-center">
          <div className="text-[var(--text-muted)] mb-4">Loading audit logs...</div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="text-red-800">{errorMessage}</div>
          <button
            onClick={fetchAuditLogs}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Audit Log Table */}
      {!isLoading && !errorMessage && auditLogEntries.length > 0 && (
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-[var(--surface-raised)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                    Action Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                    Entity Affected
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {auditLogEntries.map(entry => (
                  <tr key={entry.id} className="hover:bg-[var(--surface-raised)] transition-colors">
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-primary)] whitespace-nowrap">
                      {entry.timestamp}
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-primary)] whitespace-nowrap">
                      {getUserEmail(entry.userId)}
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-primary)] whitespace-nowrap">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[var(--accent-primary)]/15 text-[var(--accent-primary)]">
                        {entry.actionType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-primary)] whitespace-nowrap">
                      {entry.entityAffected}
                    </td>
                    <td className="px-4 py-3 text-base sm:text-sm text-[var(--text-secondary)]">
                      {entry.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !errorMessage && auditLogEntries.length === 0 && (
        <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-8 text-center">
          <div className="text-[var(--text-muted)] mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">No matching entries</h3>
          <p className="text-base sm:text-sm text-[var(--text-secondary)]">
            No audit log entries match the applied filters. Try adjusting your filter criteria.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-5 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          onClick={() => navigate('/admin/dashboard')}
          type="button"
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}

export default AdminAuditLogPage;